import { Box, Button, Stack, Typography } from "@mui/joy";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import AppleLoginButton from "components/user/AppleLoginButton";
import GoogleLoginButton from "components/user/GoogleLoginButton";
import KakaoLoginButton from "components/user/KakaoLoginButton";
import login_main from "static/image/login/login_brain.png";
import { isDesktop, isMobile } from "react-device-detect";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import { loginBlockModalAtom } from "recoil/loginBlockModalAtom";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import PCLoginButton from "../../components/user/PCLoginButton";
import { extractHexColor } from "../../styles/hexOpacity";
import { sendMessage } from "../../utils/webview";
import Link from "../../components/common/Link";
import {
  LogoWithTextSvg,
  ShieldSmallIconSvg,
} from "../../static/image/common/SvgAssets";
import useGetDeviceInfo from "../../hooks/useGetDeviceInfo";
import useGetUrls from "../../hooks/useGetUrls";
import { compareVersions } from "../../logic/logics_common";
import { PATH } from "routes/paths";
import useDetectIOS from "../../hooks/useDetectIOS";

// JavaScript
export const isIPad =
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
  (/iPad|Macintosh/.test(navigator.userAgent) &&
    /Mobile/.test(navigator.userAgent));

export default function Login() {
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const [searchParams] = useSearchParams();
  const { state } = useLocation();

  const isInappWebview = useIsInappWebview();
  const loginBlockModal = useRecoilValue(loginBlockModalAtom);
  const resetLoginBlockModal = useResetRecoilState(loginBlockModalAtom);

  const { homeIndicatorHeight, appVersion } = useGetDeviceInfo();
  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();

  const isOnboarding = searchParams.get("type") === "1"; // 신규모객
  const { isIOS } = useDetectIOS();

  const backgroundColor = theme.vars.palette.Background.Level2;
  // Mixpanel 이벤트 예시
  const setMixPanel_come = useMixpanelWeb({
    eventName: "로그인 페이지 진입",
    params: {
      type: isMobile ? "app" : "web",
      ...(user?.appId && { appId: user?.appId }),
    },
  });
  const hasTrackedLoginComeRef = useRef(false);

  useEffect(() => {
    if (!user?.accessToken) {
      if (isOnboarding) {
        navigate("/", { replace: true, state: { isOnboarding: true } });
      }
    } else {
      // 이미 로그인이 되어 있다면 메인으로 이동
      navigate("/", { ...state, replace: true });
    }
  }, [user]);

  useEffect(() => {
    if (!hasTrackedLoginComeRef.current) {
      hasTrackedLoginComeRef.current = true;
      setMixPanel_come();
    }
  }, []);

  useEffect(() => {
    if (isInappWebview) {
      const hexValue = extractHexColor(backgroundColor);

      if (hexValue) {
        sendMessage(
          JSON.stringify({
            event: "set_safearea_color",
            params: {
              topColor: `${hexValue}`,
              bottomColor: `${hexValue}`,
            },
          })
        );
      }
    }
  }, [backgroundColor, isInappWebview]);

  const children = (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      spacing={{ xs: 2, sm: 4 }}
      sx={{
        flex: 1,
        width: "100%",
        boxSizing: "border-box",
        py: { xs: 2, sm: 4 },
      }}
    >
      {/* 이미지 + 텍스트 + 버튼을 하나의 중앙 그룹으로 */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "320px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 4 },
        }}
      >
        {/* 이미지 영역 */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={login_main}
            alt="empty"
            style={{
              width: "100%",
              maxWidth: "266px",
              maxHeight: "clamp(120px, 35vh, 280px)",
              minHeight: "200px",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* 텍스트 영역 */}
        <Stack spacing={"12px"} sx={{ textAlign: "center", width: "100%" }}>
          <CustomTypography
            variant="H1"
            sx={{
              color: theme.vars.palette.Content.Strong,
              fontWeight: 800,
            }}
          >
            {"효과가 입증된\n지속가능한 멘탈케어"}
          </CustomTypography>
          <CustomTypography
            variant="Subtitle"
            sx={{
              color: theme.vars.palette.Content.Medium,
              fontWeight: 500,
            }}
          >
            {"전담 코치가 이끄는 맞춤 프로그램으로\n새로운 삶을 맞이하세요."}
          </CustomTypography>
        </Stack>

        {/* 로그인 버튼 영역 */}
        <Stack
          spacing={"6px"}
          sx={{
            width: "100%",
          }}
          alignSelf="center"
        >
          {isDesktop ? (
            <>
              <PCLoginButton />
              <Stack
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                width={"100%"}
                sx={{ textAlign: "center", gap: "2px" }}
              >
                <ShieldSmallIconSvg
                  color={theme.vars.palette.Content.Highlight}
                />
                <Typography
                  variant={"Caption"}
                  sx={{
                    textAlign: "center",
                    color: theme.vars.palette.Content.Highlight,
                    fontWeight: 500,
                  }}
                >
                  {"100일 완성 295,000원부터 | 불만족시 전액 환불"}
                </Typography>
              </Stack>
            </>
          ) : (
            <>
              <KakaoLoginButton />
              <AppleLoginButton />
              <GoogleLoginButton />
              {isInappWebview && compareVersions(appVersion, "1.1.98") >= 0 && (
                <Button
                  variant="Plain"
                  color="neutral"
                  onClick={() => navigate(PATH.emailLogin)}
                  sx={{ mt: 1 }}
                >
                  다른 방법으로 시작하기
                </Button>
              )}
              <Stack
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
                justifyContent={"center"}
                width={"100%"}
                sx={{ textAlign: "center", gap: "2px" }}
              >
                <ShieldSmallIconSvg
                  color={theme.vars.palette.Content.Highlight}
                />
                <Typography
                  variant={"Caption"}
                  sx={{
                    textAlign: "center",
                    color: theme.vars.palette.Content.Highlight,
                    fontWeight: 500,
                  }}
                >
                  {"100일 완성 295,000원부터 | 불만족시 전액 환불"}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <Stack
      direction={"column"}
      alignItems="center"
      sx={{
        zIndex: 1000,
        position: "fixed",
        top: 0,
        width: "100vw",
        height: "calc(var(--vh,1vh) * 100)",
        minHeight: "calc(var(--vh,1vh) * 100)",
        backgroundColor: backgroundColor,
        pb: `${homeIndicatorHeight}px`,
        overflowY: "auto",
        overscrollBehaviorY: "none",
        touchAction: "pan-y",
      }}
    >
      {!isInappWebview && (
        <Box sx={{ position: "fixed", top: 20, left: 20, zIndex: 1100 }}>
          <LogoWithTextSvg />
        </Box>
      )}

      {children}

      <Stack
        direction={"row"}
        spacing={"20px"}
        justifyContent="center"
        sx={{
          mt: "auto",
          mb: "0px",
          pt: 1,
          wordBreak: "keep-all",
          flexShrink: 0,
        }}
      >
        <Link
          opacity={100}
          bold={false}
          text={"사업자 정보"}
          url={companyInfoUrl}
        />
        <Link
          opacity={100}
          bold={false}
          text={"개인정보 처리방침"}
          url={privacyPolicyUrl}
        />
        <Link
          opacity={100}
          bold={false}
          text={"서비스 이용약관"}
          url={servicePolicyUrl}
        />
      </Stack>
      <CustomModal
        open={loginBlockModal.open}
        onClose={() => resetLoginBlockModal()}
        title={loginBlockModal.title || "로그인 실패"}
        subtitle={loginBlockModal.subtitle || "다시 시도해 주세요."}
        actions={[
          {
            label: "확인",
            variant: "Solid",
            onClick: () => resetLoginBlockModal(),
          },
        ]}
      />
    </Stack>
  );
}
